import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { C, cs } from '../styles/common';
import api from '../api';

const SEVERITY_COLOR = {
  critical: { bg: '#7f1d1d', text: '#fca5a5', dot: '#ef4444' },
  warning:  { bg: '#78350f', text: '#fbbf24', dot: '#f59e0b' },
  info:     { bg: '#1e3a5f', text: '#93c5fd', dot: '#3b82f6' },
};

const TYPE_ICONS = {
  cycle_drift:        'fa-solid fa-gauge-high',
  scrap_spike:        'fa-solid fa-triangle-exclamation',
  machine_idle:       'fa-solid fa-circle-pause',
  tooling_roi:        'fa-solid fa-screwdriver-wrench',
  material_low_stock: 'fa-solid fa-box-open',
  measured_ready:       'fa-solid fa-stopwatch',
  measured_moved:       'fa-solid fa-arrows-left-right',
  measured_shaken:      'fa-solid fa-scale-unbalanced',
  measured_display_gap: 'fa-solid fa-gauge-simple-high',
};

const TYPE_LABELS = {
  cycle_drift:        'Cycle time vs plan',
  scrap_spike:        'Scrap spike',
  machine_idle:       'Idle capacity',
  tooling_roi:        'Tooling ROI',
  material_low_stock: 'Low stock',
  measured_ready:       'Measured: ready to decide',
  measured_moved:       'Measured: look again',
  measured_shaken:      'Measured: check what it rests on',
  measured_display_gap: 'Machine display vs counted output',
};

export default function Dashboard() {
  const navigate        = useNavigate();
  const { isDirector }  = useAuth();
  const [insights, setInsights]   = useState([]);
  const [financials, setFinancials] = useState(null);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/api/v1/insights?status=active'),
      api.get('/api/v1/financials/summary'),
    ])
      .then(([iRes, fRes]) => {
        setInsights(iRes.data || []);
        setFinancials(fRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (!isDirector) {
    return (
      <div style={{ ...cs.page, ...cs.center }}>
        <p style={{ color: C.muted }}>Director access required.</p>
      </div>
    );
  }

  const fmt = n => n != null
    ? `€${parseFloat(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    : '—';

  const top3       = insights.slice(0, 3);
  const totalSavings = insights
    .filter(i => i.savings_eur_month > 0)
    .reduce((s, i) => s + (i.savings_eur_month || 0), 0);

  const kpis = financials ? [
    {
      label:    'Inventory value',
      value:    fmt(financials.inventory?.total_eur),
      icon:     'fa-solid fa-warehouse',
      color:    C.teal,
      sub:      `${financials.inventory?.lines?.length || 0} materials`,
    },
    {
      label:    'Revenue (30 d)',
      value:    fmt(financials.revenue?.total_eur),
      icon:     'fa-solid fa-arrow-trend-up',
      color:    '#34d399',
      sub:      `${financials.revenue?.by_part?.length || 0} parts`,
    },
    {
      label:    'Scrap cost (30 d)',
      value:    fmt(financials.scrap?.total_eur),
      icon:     'fa-solid fa-triangle-exclamation',
      color:    '#f87171',
      sub:      `${financials.scrap?.by_part?.length || 0} parts affected`,
    },
    {
      label:    'Highest cost part',
      value:    financials.cost_per_part?.[0]
                  ? fmt(financials.cost_per_part[0].cost_per_part) + '/pc'
                  : '—',
      icon:     'fa-solid fa-chart-pie',
      color:    '#fbbf24',
      sub:      financials.cost_per_part?.[0]?.name || 'No data',
    },
  ] : [];

  return (
    <div style={{ ...cs.page, padding: '20px clamp(16px, 4vw, 60px)' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={cs.pageTitle}>Director Dashboard</h1>
        <p style={cs.pageSub}>AI insights and financial overview at a glance</p>
      </div>

      {loading ? (
        <div style={cs.center}><p style={{ color: C.muted }}>Loading…</p></div>
      ) : (
        <div style={s.cols}>

          {/* ── Left: Insights ── */}
          <div style={s.col}>
            <div style={s.colHeader}>
              <div>
                <div style={s.colTitle}>
                  <i className="fa-solid fa-lightbulb" style={{ color: C.teal, marginRight: '8px' }} />
                  Active Insights
                </div>
                {insights.length > 0 && (
                  <div style={{ fontSize: '12px', color: C.muted, marginTop: '2px' }}>
                    {insights.length} finding{insights.length !== 1 ? 's' : ''}
                    {totalSavings > 0 && ` · ${fmt(totalSavings)}/mo potential`}
                  </div>
                )}
              </div>
              <button style={s.viewAllBtn} onClick={() => navigate('/insights/all')}>
                <i className="fa-solid fa-list" style={{ marginRight: '6px' }} />
                View all insights
              </button>
            </div>

            {top3.length === 0 ? (
              <div style={s.empty}>
                <i className="fa-solid fa-check-circle" style={{ fontSize: '24px', color: C.mutedDark, marginBottom: '8px' }} />
                <p style={{ color: C.muted, margin: 0 }}>No active insights</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {top3.map(insight => {
                  const sev = SEVERITY_COLOR[insight.severity] || SEVERITY_COLOR.info;
                  return (
                    <div key={insight.id} style={{ ...cs.card, borderLeft: `3px solid ${sev.dot}`, padding: '14px 16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                        <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '4px', background: sev.bg, color: sev.text }}>
                          <i className={TYPE_ICONS[insight.type] || 'fa-solid fa-circle-info'} style={{ marginRight: '4px' }} />
                          {TYPE_LABELS[insight.type] || insight.type}
                        </span>
                        {insight.savings_eur_month > 0 && (
                          <span style={{ fontSize: '13px', fontWeight: '700', color: C.teal }}>
                            {fmt(insight.savings_eur_month)}/mo
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: C.text, marginBottom: '4px', lineHeight: '1.4' }}>
                        {insight.title}
                      </div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', lineHeight: '1.5' }}>
                        {insight.body}
                      </div>
                    </div>
                  );
                })}
                <button style={s.bottomBtn} onClick={() => navigate('/insights/all')}>
                  <i className="fa-solid fa-lightbulb" style={{ marginRight: '8px' }} />
                  View all {insights.length} insights
                  <i className="fa-solid fa-arrow-right" style={{ marginLeft: '8px', fontSize: '11px' }} />
                </button>
              </div>
            )}
          </div>

          {/* ── Right: Financials ── */}
          <div style={s.col}>
            <div style={s.colHeader}>
              <div style={s.colTitle}>
                <i className="fa-solid fa-chart-line" style={{ color: C.teal, marginRight: '8px' }} />
                Financial Overview
              </div>
              <button style={s.viewAllBtn} onClick={() => navigate('/financials')}>
                <i className="fa-solid fa-chart-line" style={{ marginRight: '6px' }} />
                View all financials
              </button>
            </div>

            {!financials ? (
              <div style={s.empty}>
                <i className="fa-solid fa-chart-pie" style={{ fontSize: '24px', color: C.mutedDark, marginBottom: '8px' }} />
                <p style={{ color: C.muted, margin: 0 }}>No financial data yet</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={s.kpiGrid}>
                  {kpis.map(kpi => (
                    <div key={kpi.label} style={s.kpiCard}>
                      <i className={kpi.icon} style={{ fontSize: '18px', color: kpi.color, marginBottom: '10px' }} />
                      <div style={{ fontSize: '20px', fontWeight: '700', color: kpi.color, marginBottom: '2px' }}>
                        {kpi.value}
                      </div>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: C.text, marginBottom: '2px' }}>
                        {kpi.label}
                      </div>
                      <div style={{ fontSize: '11px', color: C.muted }}>{kpi.sub}</div>
                    </div>
                  ))}
                </div>
                <button style={s.bottomBtn} onClick={() => navigate('/financials')}>
                  <i className="fa-solid fa-chart-line" style={{ marginRight: '8px' }} />
                  View full financial report
                  <i className="fa-solid fa-arrow-right" style={{ marginLeft: '8px', fontSize: '11px' }} />
                </button>
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
}

const s = {
  cols: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 'clamp(20px, 4vw, 60px)',
    alignItems: 'start',
  },
  col: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  colHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  colTitle: {
    fontSize: '15px',
    fontWeight: '700',
    color: C.text,
  },
  viewAllBtn: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    color: C.text,
    borderRadius: '6px',
    padding: '7px 14px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'system-ui, sans-serif',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
  },
  bottomBtn: {
    background: C.teal,
    border: 'none',
    color: '#fff',
    borderRadius: '8px',
    padding: '11px 18px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: 'system-ui, sans-serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '4px',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 20px',
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: '10px',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '10px',
  },
  kpiCard: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: '10px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
  },
};
